<template>
  <div id="us-delivered">
    <div id="view-sub-nav" class="d-flex justify-space-between align-center">
      <DateRangePicker default="lastSixMonths" ref="dateRangePicker" @change="onDateRangeChange"></DateRangePicker>
      <div class="d-flex align-center flex-shrink-0">
        <v-chip size="small" color="primary"
          >Found {{ ordersWithGeo.length }} of {{ orders.length }} Filtered Orders</v-chip
        >
        <v-chip size="small" color="success" class="ml-2">Filtered Orders Tally {{ grandTotalRevenue }}</v-chip>
        <v-chip v-if="missingGeoCount > 0" size="small" color="warning" class="ml-2"
          >Missing Geo: {{ missingGeoCount }}</v-chip
        >
        <v-switch
          class="ml-6"
          v-model="includeFocRecut"
          :label="includeFocRecut ? 'Including FOC/Recut' : 'Excluding FOC/Recut'"
          hide-details
          @update:model-value="onIncludeFocRecutChange"
        >
        </v-switch>
        <v-switch class="ml-6" v-model="showMap" :label="showMap ? 'Toggle to Chart' : 'Toggle to Map'" hide-details>
        </v-switch>
      </div>
    </div>
    <div v-if="showMap" class="map-frame">
      <div
        v-if="loading"
        class="d-flex justify-center align-center"
        style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          z-index: 1000;
        "
      >
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <!-- US DELIVERED LOCATION MAP -->
      <l-map id="us-map" :zoom="zoom" :center="center">
        <l-tile-layer :url="url"></l-tile-layer>
        <l-marker-cluster-group>
          <l-marker v-for="(order, index) in ordersWithGeo" :lat-lng="order.latlng" :key="index">
            <l-tooltip>
              <div v-if="order.geoFallback" style="color: red; font-weight: bold; margin-bottom: 4px">ZIP Code N/A</div>
              <div>
                <span>Order: </span>
                <span>{{ order.orderNumber }}</span>
              </div>
              <div>
                <span>Customer: </span>
                <span>{{ order.customerEmail }}</span>
              </div>
              <div v-if="order.deliveryState">
                <span>State: </span>
                <span>{{ getStateName(order.deliveryState) }}</span>
              </div>
              <div v-if="order.deliveryCity">
                <span>City: </span>
                <span>{{ order.deliveryCity }}</span>
              </div>
              <div v-if="order.total">
                <span>Total: </span>
                <span>{{ formatCurrency(order.total) }}</span>
              </div>
              <div v-if="order.displayDate">
                <span :style="order.isTargetDate ? 'color: red' : ''">{{
                  order.isTargetDate ? 'Target Date: ' : 'Dispatch ETA: '
                }}</span>
                <span :style="order.isTargetDate ? 'color: red' : ''">{{
                  $moment.unix(order.displayDate.seconds).format('Do MMM YYYY')
                }}</span>
              </div>
            </l-tooltip>
          </l-marker>
        </l-marker-cluster-group>
      </l-map>
    </div>
    <!-- US DELIVERED LOCATION TABLE - BY STATE -->
    <div v-else>
      <div v-if="loading" class="d-flex justify-center align-center" style="height: 400px">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <div v-else>
        <v-container fluid>
          <v-data-table
            :headers="headers"
            :items="stateGroups"
            class="elevation-1"
            :items-per-page="20"
            :sort-by="[{ key: 'orderCount', order: 'desc' }]"
          ></v-data-table>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import { LMap, LTileLayer, LMarker, LTooltip } from '@vue-leaflet/vue-leaflet'
import { Icon } from 'leaflet'
import DateRangePicker from '@/components/ui/DateRangePicker.vue'
import { db } from '@/js/db'
import { collection, doc, documentId, getDocs, query, where, writeBatch } from 'firebase/firestore'

import LMarkerClusterGroup from '@/components/ui/LMarkerClusterGroup.vue'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// US state names mapping
const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
  PR: 'Puerto Rico',
  VI: 'Virgin Islands',
  GU: 'Guam',
  AS: 'American Samoa',
  MP: 'Northern Mariana Islands'
}

export default {
  name: 'UsDeliveredLocation',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LMarkerClusterGroup,
    DateRangePicker
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 4,
      center: [39.8283, -98.5795], // Centered on USA
      orders: [], // Filtered US orders
      showMap: true,
      loading: false,
      missingGeoCount: 0,
      includeFocRecut: false,
      lastDateRange: null
    }
  },
  computed: {
    // Filter orders to only those with geo coordinates
    ordersWithGeo() {
      return this.orders.filter((order) => {
        return order.latlng && Array.isArray(order.latlng) && order.latlng.length === 2
      })
    },
    // Group orders by state
    stateGroups() {
      const groups = {}

      this.orders.forEach((order) => {
        const state = order.deliveryState || 'Unknown'
        if (!groups[state]) {
          groups[state] = { count: 0, total: 0 }
        }
        groups[state].count++
        groups[state].total += order.total || 0
      })

      return Object.entries(groups).map(([code, data]) => ({
        stateCode: code,
        state: this.getStateName(code),
        orderCount: data.count,
        orderTotal: data.total,
        orderTotalFormatted: this.formatCurrency(data.total)
      }))
    },
    headers() {
      return [
        { title: 'State', key: 'state' },
        { title: 'Code', key: 'stateCode' },
        { title: 'Orders', key: 'orderCount', sortable: true },
        { title: 'Total', key: 'orderTotalFormatted', sortable: true }
      ]
    },
    // Grand total revenue across all US states
    grandTotalRevenue() {
      const total = this.orders.reduce((sum, order) => sum + (order.total || 0), 0)
      return this.formatCurrency(total)
    }
  },
  methods: {
    getStateName(code) {
      return US_STATES[code] || code
    },
    formatCurrency(amount) {
      return '$' + this.$numeral(amount).format('0,0.00')
    },
    onDateRangeChange(range) {
      if (range && range.length === 2) {
        const dateRange = {
          start: range[0],
          end: range[1]
        }
        this.bindOrders(dateRange)
      }
    },
    onIncludeFocRecutChange() {
      if (this.lastDateRange) {
        this.bindOrders(this.lastDateRange)
      }
    },
    async fetchVersionFlags(quoteIDs) {
      const result = {}
      if (!quoteIDs.length) return result
      const batches = []
      for (let i = 0; i < quoteIDs.length; i += 10) {
        batches.push(quoteIDs.slice(i, i + 10))
      }
      await Promise.all(
        batches.map(async (batch) => {
          const snapshot = await getDocs(query(collection(db, 'quotes'), where(documentId(), 'in', batch)))
          snapshot.forEach((snap) => {
            result[snap.id] = snap.data().versions || []
          })
        })
      )
      return result
    },
    async bindOrders(dateRange) {
      if (!dateRange || !dateRange.start || !dateRange.end) {
        return
      }

      this.lastDateRange = dateRange
      this.loading = true

      try {
        const startTimestamp = this.$utils.tsFromDate(dateRange.start.toDate())
        const endTimestamp = this.$utils.tsFromDate(dateRange.end.toDate())

        // Query orders directly by dispatchEta range
        let ordersSnapshot = await getDocs(
          query(
            collection(db, 'orders'),
            where('type', '==', 'mega-admin'),
            where('dispatchEta', '>=', startTimestamp),
            where('dispatchEta', '<=', endTimestamp)
          )
        )

        let allOrders = ordersSnapshot.docs.map((snap) => {
          const data = snap.data()
          return {
            id: snap.id,
            quoteID: data.quoteID || null,
            versionID: data.versionID || null,
            orderNumber: data.orderNumber,
            customerEmail: data.customerEmail,
            total: data.total || 0,
            deliveryCountry: data.customerContactDetails?.deliveryCountry || null,
            deliveryState: data.customerContactDetails?.deliveryState || null,
            deliveryCity: data.customerContactDetails?.deliveryCity || null,
            postcode: data.customerContactDetails?.deliveryPostcode || null,
            latlng: data.latlng || null,
            dispatchEta: data.dispatchEta || null,
            targetDate: data.targetDate || null,
            displayDate: data.dispatchEta || data.targetDate || null,
            isTargetDate: !data.dispatchEta && !!data.targetDate,
            geoFallback: false,
            foc: false,
            recut: false
          }
        })

        // Resolve foc/recut from the quote version, not the order doc
        const quoteIDs = [...new Set(allOrders.map((o) => o.quoteID).filter(Boolean))]
        const quoteVersions = await this.fetchVersionFlags(quoteIDs)
        allOrders.forEach((order) => {
          const versions = quoteVersions[order.quoteID] || []
          const version = versions.find((v) => v.id === order.versionID)
          order.foc = !!(version && version.foc)
          order.recut = !!(version && version.recut)
        })

        // Filter to US orders (FOC/recut excluded unless toggle is on)
        const usOrders = allOrders.filter((order) => {
          if (!this.includeFocRecut) {
            if (order.foc) return false
            if (order.recut) return false
          }
          return order.deliveryCountry === 'US' || order.deliveryCountry === 'USA'
        })

        this.orders = usOrders

        // Geocode items that need it - process in batches of 5 for speed
        const itemsNeedingGeo = this.orders.filter((order) => order.postcode && !order.latlng)
        const batchSize = 5
        const ordersToUpdate = []

        for (let i = 0; i < itemsNeedingGeo.length; i += batchSize) {
          const batch = itemsNeedingGeo.slice(i, i + batchSize)
          const results = await Promise.all(batch.map((order) => this.setLatLngFromZipCode(order)))

          // Collect orders that need database update
          results.forEach((result) => {
            if (result && result.needsUpdate) {
              ordersToUpdate.push(result)
            }
          })
        }

        // Batch update orders in Firestore
        if (ordersToUpdate.length > 0) {
          const firestoreBatch = writeBatch(db)
          ordersToUpdate.forEach(({ orderId, latlng }) => {
            firestoreBatch.update(doc(db, 'orders', orderId), { latlng })
          })
          await firestoreBatch.commit()
        }

        // Force Vue reactivity
        this.orders = [...this.orders]

        // Count orders that still don't have geo after all attempts
        const missingGeoOrders = this.orders.filter(
          (order) => !order.latlng || !Array.isArray(order.latlng) || order.latlng.length !== 2
        )
        this.missingGeoCount = missingGeoOrders.length

        // Log missing geo orders for debugging
        if (missingGeoOrders.length > 0) {
          console.log(`Missing Geo Orders (${missingGeoOrders.length}):`)
          missingGeoOrders.forEach((order) => {
            console.log(
              `  Order: ${order.orderNumber} | ZIP: ${order.postcode} | City: ${order.deliveryCity} | State: ${order.deliveryState}`
            )
          })
        }
      } catch (error) {
        console.error('Error loading US orders:', error)
      } finally {
        this.loading = false
      }
    },
    async setLatLngFromZipCode(order) {
      try {
        if (!order.postcode) return null
        // Skip if already has latlng
        if (order.latlng?.length === 2) return null

        let lat, lng
        let geoFallback = false

        // Clean ZIP code - extract 5-digit ZIP
        let cleanZip = order.postcode.replace(/[^0-9]/g, '').substring(0, 5)

        // Ensure we have a valid 5-digit ZIP
        if (cleanZip.length !== 5) {
          return null
        }

        // Try Zippopotam.us (US ZIP code lookup)
        try {
          const response = await fetch(`https://api.zippopotam.us/us/${cleanZip}`)
          if (response.ok) {
            const data = await response.json()
            if (data.places?.[0]) {
              lat = parseFloat(data.places[0].latitude)
              lng = parseFloat(data.places[0].longitude)

              // Also extract state from response if we don't have it
              if (!order.deliveryState && data.places[0]['state abbreviation']) {
                order.deliveryState = data.places[0]['state abbreviation']
              }
            }
          }
        } catch (e) {
          // Zippopotam.us failed, will try fallback
        }

        // Fallback: Nominatim with ZIP + country
        if (!lat && cleanZip) {
          try {
            const query = `postalcode=${encodeURIComponent(cleanZip)}&countrycodes=us&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
              }
            }
          } catch (e) {
            // Nominatim fallback failed
          }
        }

        // Fallback 2: City + State lookup if we have them
        if (!lat && order.deliveryCity && order.deliveryState) {
          try {
            const query = `city=${encodeURIComponent(order.deliveryCity)}&state=${encodeURIComponent(
              order.deliveryState
            )}&countrycodes=us&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
                geoFallback = true
              }
            }
          } catch (e) {
            // City/State fallback failed
          }
        }

        if (lat && lng) {
          // Update local data
          order.latlng = [lat, lng]
          order.geoFallback = geoFallback

          // Return update info for batch processing (only if not fallback)
          if (!geoFallback) {
            return { needsUpdate: true, orderId: order.id, latlng: [lat, lng] }
          }
        }

        return null
      } catch (error) {
        console.error('Error geocoding US ZIP:', order.postcode, error)
        return null
      }
    }
  },
  created() {
    this.$nextTick(() => {
      if (this.$refs.dateRangePicker) {
        this.$refs.dateRangePicker.setPreset(1)
        const twelveMonthRange = this.$refs.dateRangePicker.lastTwelveMonths()
        const dateRange = {
          start: twelveMonthRange[0],
          end: twelveMonthRange[1]
        }
        this.bindOrders(dateRange)
      }
    })
  }
}
</script>

<style lang="scss">
@import '~leaflet/dist/leaflet.css';
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

#us-delivered {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

#us-map {
  z-index: 0;
  height: 100%;
  width: 100%;
}

.map-frame {
  position: relative;
  width: 100%;
  height: $analytics-map-height;
}
</style>
